import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getEffectiveAvatarProvider } from '@/lib/avatarGating';
import { preloadGLTF } from '@/lib/avatarPreloader';
import {
  invalidateSnapshotCache,
  checkGlbExists,
  clearStaleAvatarReference,
} from '@/hooks/useAvatarSnapshot';

interface AvatarData {
  avatarUrl: string | null;
  provider: 'none' | 'avaturn' | 'metaperson';
  loading: boolean;
  error: string | null;
  hasAvatar: boolean;
  isPremiumAvatar: boolean;
  canUseMetaPerson: boolean;
  shouldDowngradeAvatar: boolean;
  effectiveProvider: 'none' | 'avaturn' | 'metaperson';
  tierEligible: 'free' | 'premium' | 'pro';
  // Hydrated from user_profiles.sex. Já alimentou o aria-label do orb antigo
  // (removido em 08/08 — a energia tem rótulo único); segue exposto para quem
  // precise rotular por gênero. `null` quando o usuário não completou a
  // identidade no onboarding, ou escolheu 'other'.
  sex: 'male' | 'female' | 'other' | null;
  refetch: () => Promise<void>;
}

const FALLBACK_AVATAR = '/avatar/model_small.glb';

// Module-level cache — shared across all components using useAvatar.
// Prevents re-fetching from Supabase on every mount and eliminates
// the null→placeholder→real URL flicker that causes Three.js scene resets.
let cachedData: Omit<AvatarData, 'refetch'> | null = null;
let fetchPromise: Promise<Omit<AvatarData, 'refetch'> | null> | null = null;
let generation = 0;
const listeners = new Set<() => void>();

const DEFAULT_RESULT: Omit<AvatarData, 'refetch'> = {
  avatarUrl: FALLBACK_AVATAR,
  provider: 'none',
  loading: false,
  error: null,
  hasAvatar: false,
  isPremiumAvatar: false,
  canUseMetaPerson: false,
  shouldDowngradeAvatar: false,
  effectiveProvider: 'none',
  tierEligible: 'free',
  sex: null,
};

async function fetchAvatarData(): Promise<Omit<AvatarData, 'refetch'> | null> {
  try {
    // Session lookup is local. The two authenticated database requests below
    // validate the JWT and enforce RLS; /auth/v1/user adds no data we need.
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    // Return null (NOT a cacheable default) when unauthenticated. Caching a
    // pre-auth "no avatar" result is exactly what stuck across login and left
    // the Home avatar on the placeholder — the module cache short-circuits the
    // refetch, so a null cached here never re-hydrates after SIGNED_IN.
    if (!user) return null;

    // Plan comes from user_profiles (unchanged). `sex` joined in so the
    // silhouette fallback can pick the gender variant without a second fetch.
    const [profileResult, activeResult] = await Promise.all([
      supabase.from('user_profiles')
        .select('avatar_url, avatar_provider, plan, sex').eq('user_id', user.id).maybeSingle(),
      supabase.from('user_avatars')
        .select('avatar_url, provider').eq('user_id', user.id).eq('is_active', true).maybeSingle(),
    ]);
    const { data: profile, error: profileError } = profileResult;
    const { data: activeAvatar, error: activeAvatarError } = activeResult;
    if (profileError) {
      // TRANSIENT failure → return null (UNCACHED), same as pre-auth. Falling
      // through with plan='free' used to CACHE canUseMetaPerson:false for the
      // whole SPA session — one hiccup (e.g. a WKWebView reload mid-creator)
      // and the "create from selfie" card vanished until a manual cache bust
      // (the prod "option removed after failure" symptom, 12/06).
      console.error('[useAvatar] user_profiles read failed:', profileError);
      return null;
    }

    const p = profile as Record<string, unknown> | null;
    const plan = (p?.plan as string) || 'free';
    const canMeta = plan === 'premium' || plan === 'pro';
    const validTier = (plan === 'premium' || plan === 'pro' ? plan : 'free') as 'free' | 'premium' | 'pro';
    const rawSex = p?.sex as string | undefined;
    const sex: 'male' | 'female' | 'other' | null =
      rawSex === 'male' || rawSex === 'female' || rawSex === 'other' ? rawSex : null;

    // Primary source: user_avatars row with is_active=true.
    // Fallback (edge-case migration safety): user_profiles.avatar_url, which
    // the DB trigger keeps mirrored to the active avatar, but may be the only
    // value if the trigger hadn't fired for a given user yet.
    if (activeAvatarError) {
      console.error('[useAvatar] user_avatars (active) read failed:', activeAvatarError);
    }

    const a = activeAvatar as { avatar_url?: string | null; provider?: string | null } | null;

    const resolvedUrl = (a?.avatar_url as string | undefined) || (p?.avatar_url as string | undefined) || null;
    const resolvedProviderRaw =
      (a?.provider as string | undefined) || (p?.avatar_provider as string | undefined) || 'none';

    if (!resolvedUrl || resolvedProviderRaw === 'none') {
      return { ...DEFAULT_RESULT, canUseMetaPerson: canMeta, tierEligible: validTier, sex };
    }

    // Stale-pointer guard at the SOURCE (12/06): generated GLBs can vanish
    // from storage while the DB still points at them (VYV-FRONTEND-10 fetch
    // 400s). The frozen viewers (AvatarCore/AvatarViewer/MirrorAvatar) load
    // whatever URL this hook hands them — gate it HERE so they never receive
    // a dead one. Same #193 semantics: 400/404 = stale CONFIRMED → canonical
    // reset + silhouette state; network failure = 'unknown' → keep the URL
    // (never punish a flaky connection with a destructive write).
    if (resolvedUrl.includes('/avatars/generated/')) {
      const glbStatus = await checkGlbExists(resolvedUrl);
      if (glbStatus === 'stale') {
        await clearStaleAvatarReference(user.id, resolvedUrl);
        return { ...DEFAULT_RESULT, canUseMetaPerson: canMeta, tierEligible: validTier, sex };
      }
    }

    // Type union kept wide ('avaturn' included) only so the 5 FROZEN avatar
    // files keep compiling — runtime never returns 'avaturn' post-W4
    // (Sub-task B bulk-migrated all rows to 'metaperson' / 'none').
    const storedProvider = resolvedProviderRaw as 'avaturn' | 'metaperson';
    const effective = getEffectiveAvatarProvider(plan, storedProvider);
    // Post-W4: catalog MP is free for all plans, so no downgrade scenario.
    const shouldDowngrade = false;

    return {
      avatarUrl: resolvedUrl,
      provider: storedProvider,
      loading: false,
      error: null,
      hasAvatar: true,
      isPremiumAvatar: storedProvider === 'metaperson',
      canUseMetaPerson: canMeta,
      shouldDowngradeAvatar: shouldDowngrade,
      effectiveProvider: effective,
      tierEligible: validTier,
      sex,
    };
  } catch (err) {
    // TRANSIENT failure → null (UNCACHED). Returning a cacheable default here
    // froze canUseMetaPerson:false (and hasAvatar:false) for the entire SPA
    // session after one error — see the profileError comment above.
    console.error('[useAvatar] Failed to load avatar data:', err);
    return null;
  }
}

function loadAvatarData(): Promise<Omit<AvatarData, 'refetch'> | null> {
  if (cachedData) return Promise.resolve(cachedData);
  if (fetchPromise) return fetchPromise;
  const epoch = generation;
  const promise = fetchAvatarData().then((result) => {
    if (generation !== epoch) return null;
    if (result) cachedData = result;
    return result;
  }).finally(() => {
    if (fetchPromise === promise) fetchPromise = null;
  });
  fetchPromise = promise;
  return promise;
}

export interface UseAvatarOptions {
  /** Metadata-only callers can avoid fetching/decoding a model they never render. */
  preloadModel?: boolean;
}

export function useAvatar({ preloadModel = true }: UseAvatarOptions = {}): AvatarData {
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<Omit<AvatarData, 'refetch'>>(
    () => cachedData ?? { ...DEFAULT_RESULT, avatarUrl: null, loading: true },
  );
  useEffect(() => {
    const onInvalidate = () => setRevision((value) => value + 1);
    listeners.add(onInvalidate);
    return () => { listeners.delete(onInvalidate); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const epoch = generation;
    const load = async () => {
      const result = await loadAvatarData();
      if (cancelled || generation !== epoch) return;
      if (result) { setData(result); return; }
      attempts += 1;
      if (attempts <= 3) timer = setTimeout(() => { void load(); }, 300 * attempts);
      else setData({ ...DEFAULT_RESULT });
    };
    void load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [revision]);

  useEffect(() => {
    if (!preloadModel || data.loading || !data.hasAvatar || !data.avatarUrl) return;
    void preloadGLTF(data.avatarUrl).catch(() => {});
  }, [preloadModel, data.loading, data.hasAvatar, data.avatarUrl]);

  const refetch = useCallback(async () => {
    invalidateAvatarCache();
    const epoch = generation;
    const fresh = await loadAvatarData();
    if (generation === epoch) setData(fresh ?? { ...DEFAULT_RESULT });
  }, []);
  return { ...data, refetch };
}

export function invalidateAvatarCache() {
  generation += 1;
  cachedData = null;
  fetchPromise = null;
  invalidateSnapshotCache();
  for (const listener of listeners) listener();
}
